import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Compass, Car, ArrowRight, CheckCircle2, Target, Zap } from 'lucide-react';
import { Button } from './ui/button';

const BoussoleCard = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    fetchBoussoleStatus();
  }, []);

  const fetchBoussoleStatus = async () => {
    try {
      const res = await api.get('/api/boussole/current');
      const { boussole, is_available, is_sunday, week } = res.data;
      
      // Show CTA card on Sunday/Monday/Tuesday, or if there's an in-progress boussole
      // Show summary card all week if completed
      const shouldShow = is_available || 
        (boussole && boussole.statut === 'en_cours') || 
        (boussole && boussole.statut === 'completee');
      setVisible(shouldShow);
      setData({ boussole, is_available, is_sunday, week });
    } catch (err) {
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !visible) return null;

  const boussole = data?.boussole;
  const isCompleted = boussole?.statut === 'completee';
  const isSkipped = boussole?.statut === 'skippee';
  const isInProgress = boussole?.statut === 'en_cours';

  if (isSkipped) return null;

  // Completed state - show summary
  if (isCompleted) {
    return (
      <div data-testid="boussole-card-summary" className="glass-card p-6 border-atlasCyan/20 glow-cyan">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-atlasCyan/10 rounded-lg">
            <Compass className="w-5 h-5 text-atlasCyan" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">{t('boussole.cardSummaryTitle')}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span className="text-xs text-green-400">{t('boussole.completed')}</span>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          {/* Priorities */}
          {boussole.priorites?.length > 0 && (
            <div>
              <p className="text-xs text-atlasTextSecondary mb-1.5 flex items-center gap-1.5">
                <Target className="w-3 h-3" /> {t('boussole.yourPriorities')}
              </p>
              <ul className="space-y-1">
                {boussole.priorites.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white">
                    <span className="text-atlasCyan font-bold text-xs mt-0.5">#{i + 1}</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Decision */}
          {boussole.decision_attendue && (
            <div>
              <p className="text-xs text-atlasTextSecondary mb-1 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> {t('boussole.decisionToMake')}
              </p>
              <p className="text-sm text-white">{boussole.decision_attendue.label}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not started or in progress - show CTA
  return (
    <div data-testid="boussole-card-cta" className="glass-card p-6 border-atlasCyan/20 glow-cyan">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-atlasCyan/10 rounded-xl flex-shrink-0">
          <Compass className="w-7 h-7 text-atlasCyan" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white">{t('boussole.title')}</h3>
          <p className="text-sm text-atlasTextSecondary mt-1">
            {isInProgress ? t('boussole.resumeSubtitle') : t('boussole.subtitle')}
          </p>
          {data?.is_sunday && !isInProgress && (
            <p className="text-xs text-atlasTextSecondary/70 italic mt-1">{t('boussole.sundayHint')}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Button
              data-testid="boussole-start-btn"
              onClick={() => navigate('/boussole')}
              className="btn-primary flex items-center gap-2"
            >
              {isInProgress ? t('boussole.resume') : t('boussole.start')}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              data-testid="boussole-driving-btn"
              onClick={() => navigate('/driving')}
              variant="outline"
              className="border-atlasTextSecondary text-atlasTextSecondary hover:text-white flex items-center gap-2"
            >
              <Car className="w-4 h-4" />
              {t('boussole.drivingMode')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoussoleCard;
